import React, { createContext, useReducer, useContext, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { getRequest, postRequest } from '../../Fetch/request';
import { DEFAULTS } from '../../Utils/constants';

const EditorContext = createContext();

const initialState = {
  isLoading: true,
  hasUnsavedChanges: false,
  files: DEFAULTS.CODE_FILES.map(file => ({
    ...file,
    id: uuid() // Generate unique IDs for each file
  })),
  folders: DEFAULTS.CODE_FOLDERS,
  collapsed: [],
  activeFileId: null,
  selectedFolder: null,
  output: '',
  loading: false,
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_ACTIVE':
      return { ...state, activeFileId: action.id, selectedFolder: null };
    case 'UPDATE_CONTENT': {
      const files = state.files.map((f) =>
        f.id === action.id ? { ...f, content: action.content } : f,
      );
      return { ...state, files, hasUnsavedChanges: true };
    }
    case 'ADD_FILE': {
      const newFile = { id: uuid(), filename: action.filename, content: action.content || '' };
      return { ...state, files: [...state.files, newFile], activeFileId: newFile.id, selectedFolder: null, hasUnsavedChanges: true };
    }
    case 'ADD_FOLDER': {
      if (state.folders.includes(action.folder)) return state;
      return { ...state, folders: [...state.folders, action.folder], hasUnsavedChanges: true };
    }
    case 'SET_SELECTED_FOLDER':
      return { ...state, selectedFolder: action.folder };
    case 'DELETE_FILE': {
      const files = state.files.filter(f => f.id !== action.id);
      let active = state.activeFileId;
      if(active === action.id) active = files.length? files[0].id : null;
      return { ...state, files, activeFileId: active, hasUnsavedChanges: true };
    }
    case 'DELETE_FOLDER': {
      const folderPath = action.folder;
      const files = state.files.filter(f => !f.filename.startsWith(folderPath + '/'));
      const folders = state.folders.filter(f => !(f === folderPath || f.startsWith(folderPath + '/')));
      const collapsed = state.collapsed.filter(f => !(f === folderPath || f.startsWith(folderPath + '/')));
      let selectedFolder = state.selectedFolder;
      if(selectedFolder && (selectedFolder===folderPath || selectedFolder.startsWith(folderPath+'/'))){
        selectedFolder = null;
      }
      let active = state.activeFileId;
      if(active && !files.some(f=>f.id===active)) active = files.length? files[0].id : null;
      return { ...state, files, folders, collapsed, selectedFolder, activeFileId: active, hasUnsavedChanges: true };
    }
    case 'TOGGLE_COLLAPSE': {
      const exists = state.collapsed.includes(action.folder);
      const collapsed = exists
        ? state.collapsed.filter((f) => f !== action.folder)
        : [...state.collapsed, action.folder];
      return { ...state, collapsed };
    }
    case 'SET_OUTPUT':
      return { ...state, output: action.output };
    case 'SET_LOADING':
      return { ...state, loading: action.loading };
    case 'SET_LOADING_FILES':
      return { ...state, isLoading: action.isLoading };
    case 'INIT_FILES': {
      const { files, folders } = action;
      return {
        ...state,
        files,
        folders,
        collapsed: [],
        activeFileId: files.length ? files[0].id : null,
        selectedFolder: null,
      };
    }
    case 'RENAME_FILE': {
      const files = state.files.map(f => 
        f.id === action.id ? { ...f, filename: action.newFilename } : f
      );
      return { ...state, files, hasUnsavedChanges: true };
    }
    case 'RENAME_FOLDER': {
      const oldPath = action.oldPath;
      const newPath = action.newPath;
      
      // Update folder paths
      const folders = state.folders.map(f => {
        if (f === oldPath) return newPath;
        if (f.startsWith(oldPath + '/')) {
          return f.replace(oldPath, newPath);
        }
        return f;
      });
      
      // Update file paths
      const files = state.files.map(f => {
        if (f.filename === oldPath) {
          return { ...f, filename: newPath };
        }
        if (f.filename.startsWith(oldPath + '/')) {
          return { ...f, filename: f.filename.replace(oldPath, newPath) };
        }
        return f;
      });
      
      // Update collapsed state
      const collapsed = state.collapsed.map(f => {
        if (f === oldPath) return newPath;
        if (f.startsWith(oldPath + '/')) {
          return f.replace(oldPath, newPath);
        }
        return f;
      });
      
      // Update selected folder
      let selectedFolder = state.selectedFolder;
      if (selectedFolder === oldPath) {
        selectedFolder = newPath;
      } else if (selectedFolder && selectedFolder.startsWith(oldPath + '/')) {
        selectedFolder = selectedFolder.replace(oldPath, newPath);
      }
      
      return { ...state, files, folders, collapsed, selectedFolder, hasUnsavedChanges: true };
    }
    case 'MOVE_FILE': {
      const files = state.files.map(f => 
        f.id === action.id ? { ...f, filename: action.newFilename } : f
      );
      return { ...state, files, hasUnsavedChanges: true };
    }
    case 'MOVE_FOLDER': {
      const oldPath = action.oldPath;
      const newPath = action.newPath;
      
      // Update folder paths
      const folders = state.folders.map(f => {
        if (f === oldPath) return newPath;
        if (f.startsWith(oldPath + '/')) {
          return f.replace(oldPath, newPath);
        }
        return f;
      });
      
      // Update file paths
      const files = state.files.map(f => {
        if (f.filename.startsWith(oldPath + '/')) {
          return { ...f, filename: f.filename.replace(oldPath, newPath) };
        }
        return f;
      });
      
      // Update collapsed state
      const collapsed = state.collapsed.map(f => {
        if (f === oldPath) return newPath;
        if (f.startsWith(oldPath + '/')) {
          return f.replace(oldPath, newPath);
        }
        return f;
      });
      
      // Update selected folder
      let selectedFolder = state.selectedFolder;
      if (selectedFolder === oldPath) {
        selectedFolder = newPath;
      } else if (selectedFolder && selectedFolder.startsWith(oldPath + '/')) {
        selectedFolder = selectedFolder.replace(oldPath, newPath);
      }
      
      return { ...state, files, folders, collapsed, selectedFolder, hasUnsavedChanges: true };
    }
    case 'IMPORT_PROJECT': {
      const { projectData } = action;
      
      // Validate and process imported data
      const files = projectData.files.map(file => ({
        id: file.id || uuid(),
        filename: file.filename,
        content: file.content || ''
      }));
      
      const folders = projectData.folders || [];
      
      return {
        ...state,
        files,
        folders,
        collapsed: [],
        activeFileId: files.length ? files[0].id : null,
        selectedFolder: null,
        output: '',
        loading: false,
        hasUnsavedChanges: true
      };
    }
    case 'CLEAR_UNSAVED_CHANGES': {
      return { ...state, hasUnsavedChanges: false };
    }
    default:
      return state;
  }
}

export function EditorProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Save to localStorage whenever files or folders change
  useEffect(() => {
    if (!state.isLoading) {
      const problemId = localStorage.getItem('current-lld-problemId');
      if (problemId) {
        localStorage.setItem(`lld-editor-files-${problemId}`, JSON.stringify({
          files: state.files,
          folders: state.folders,
          hasUnsavedChanges: state.hasUnsavedChanges,
          timestamp: Date.now()
        }));
      }
    }
  }, [state.files, state.folders, state.hasUnsavedChanges, state.isLoading]);

  // Warn user before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (state.hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.hasUnsavedChanges]);

  // Fetch existing attempt to load saved files (if any)
  useEffect(() => {
    const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
    const match = window.location.pathname.match(/\/([^/]+)\/editor$/);
    let problemId = match ? match[1] : null;
    
    // If no problemId from URL, check localStorage for LLD problemId
    if (!problemId) {
      problemId = localStorage.getItem('current-lld-problemId');
    }
    
    if (!userId || !problemId) {
      dispatch({ type: 'SET_LOADING_FILES', isLoading: false });
      return;
    }

    (async () => {
      try {
        // First, try to load from localStorage for faster loading
        const localData = localStorage.getItem(`lld-editor-files-${problemId}`);
        if (localData) {
          try {
            const parsedData = JSON.parse(localData);
            if (parsedData.files && Array.isArray(parsedData.files)) {
              const foldersSet = new Set();
              parsedData.files.forEach((file) => {
                const parts = file.filename.split('/');
                if (parts.length > 1) {
                  for (let i = 1; i < parts.length; i++) {
                    foldersSet.add(parts.slice(0, i).join('/'));
                  }
                }
              });

              dispatch({
                type: 'INIT_FILES',
                files: parsedData.files,
                folders: Array.from(foldersSet),
              });
              dispatch({ type: 'SET_LOADING_FILES', isLoading: false });
              return; // Use local data, don't fetch from server
            }
          } catch (localError) {
            console.error('Failed to parse local data:', localError);
          }
        }

        // If no local data or invalid, fetch from server
        const res = await getRequest(
          `${process.env.REACT_APP_URL}/lld-attempt?userId=${userId}&lldProblemId=${problemId}`,
        );
        const serverFiles = res.response?.codeFiles || res.response?.files;
        if (res?.status === '200' && Array.isArray(serverFiles) && serverFiles.length) {
          const filesFromServer = serverFiles.map((f) => ({
            id: f.id || uuid(),
            filename: f.filename,
            content: f.content,
          }));

          const foldersSet = new Set();
          filesFromServer.forEach((file) => {
            const parts = file.filename.split('/');
            if (parts.length > 1) {
              for (let i = 1; i < parts.length; i++) {
                foldersSet.add(parts.slice(0, i).join('/'));
              }
            }
          });

          dispatch({
            type: 'INIT_FILES',
            files: filesFromServer,
            folders: Array.from(foldersSet),
          });
        } else {
          // No files from server, use default files
          dispatch({
            type: 'INIT_FILES',
            files: initialState.files,
            folders: initialState.folders,
          });
          
          // Save default files to localStorage for future use
          localStorage.setItem(`lld-editor-files-${problemId}`, JSON.stringify({
            files: initialState.files,
            folders: initialState.folders,
            hasUnsavedChanges: false,
            timestamp: Date.now()
          }));
        }
      } catch (err) {
        // ignore load errors, keep default state
        console.error('Failed loading attempt files', err);
      } finally {
        dispatch({ type: 'SET_LOADING_FILES', isLoading: false });
      }
    })();
  }, []);

  const saveToBackend = async () => {
    const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
    const problemId = localStorage.getItem('current-lld-problemId');
    
    if (!userId || !problemId) {
      throw new Error('Missing user ID or problem ID');
    }

    try {
      // Save codeFiles via metadata endpoint
      const response = await postRequest(
        `${process.env.REACT_APP_URL}/lld-attempt/update-metadata`,
        {
          userId,
          lldProblemId: problemId,
          codeFiles: state.files.map(f => ({
            id: f.id,
            filename: f.filename,
            content: f.content
          }))
        }
      );
      
      if (response?.status === '200') {
        dispatch({ type: 'CLEAR_UNSAVED_CHANGES' });
        return true;
      } else {
        throw new Error('Failed to save to backend');
      }
    } catch (error) {
      console.error('Error saving to backend:', error);
      throw error;
    }
  };

  return (
    <EditorContext.Provider value={{ state, dispatch, saveToBackend }}>
      {children}
    </EditorContext.Provider>
  );
}

export function useEditor() {
  return useContext(EditorContext);
} 